import {
	PartyRelationTypeEnum,
	useFetchAllPreferredCarrierWithGroups,
} from '@uturn/api/v2';
import { Permission } from '@uturn/portal/abac';
import { isFieldReadOnly } from '@uturn/portal/modules/shipments/utils';
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	MultiSelect,
} from '@uturn/ui-kit';
import { useAbac } from 'react-abac';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type PreferredCarriersFieldProps = {
	// biome-ignore lint/suspicious/noExplicitAny: Lazy typing?
	form: UseFormReturn<any>;
	readOnlyFields: string[];
};

export const PreferredCarriersField = ({
	form,
	readOnlyFields,
}: PreferredCarriersFieldProps) => {
	const { t } = useTranslation();
	const { userHasPermissions } = useAbac();

	const { data: carriers } = useFetchAllPreferredCarrierWithGroups({
		query: {
			enabled: userHasPermissions(Permission.INVITE_CARRIER),
			select: (data) => [
				...(data.data.carrierGroup?.map((group) => ({
					value: group.groupId,
					name: group.groupName,
					options: group.carriers.map((carrier) => ({
						value: carrier.organizationId,
						label: carrier.organizationName,
					})),
				})) ?? []),
				...(data.data.individualCarriers
					?.filter(
						(carrier) =>
							carrier.relationType === PartyRelationTypeEnum.CONTRACTED,
					)
					.map((carrier) => ({
						value: carrier.organizationId,
						label: carrier.organizationName,
					})) ?? []),
			],
		},
	});

	if (!carriers || carriers.length === 0) {
		return null;
	}

	return (
		<div className="sm:col-span-full">
			<FormField
				control={form.control}
				name="preferredCarrierIds"
				render={({ field }) => (
					<FormItem>
						<FormLabel>
							{t(
								'pages.addshipment.manual.preferred_carries.label',
								'Preferred carriers',
							)}
						</FormLabel>
						<FormControl>
							<MultiSelect
								onChange={field.onChange}
								placeholder={t(
									'pages.addshipment.manual.preferred_carries.placeholder',
									'Select carrier',
								).toString()}
								selected={field.value}
								options={carriers}
								selectAllEnabled={true}
								align="start"
								searchPlaceholder={t(
									'pages.addshipment.manual.preferred_carries.search.placeholder',
									'Search carrier...',
								).toString()}
								emptyMessage={t(
									'pages.addshipment.manual.preferred_carries.search.empty',
									'No carriers found.',
								).toString()}
								disabled={isFieldReadOnly(
									readOnlyFields,
									'preferredCarrierIds',
								)}
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	);
};
